import React, { useContext, useEffect, useState, useRef } from 'react';

import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import { getDateToString  } from '../../utils/convert';
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { useEntidad } from '../../components/hooks/useEntidad';
import { useNav } from '../../components/hooks/useNav';
import { ManagedContext } from '../../context/custom';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import {TIPS} from "../../consts/tips";


function ApremiosView() {

    //#region Hooks
    const navigate = useNav()
    const { showMessageModal } = useContext(ManagedContext)

    const [state, setState] = useState({
        loading: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const searchRef = useRef();
    const tableRef = useRef();

    const [tableVisible, setTableVisible] = useState(false)

    const [personasControladores, setPersonasControladores] = useState([]);

    const [getListEntidad, getRowEntidad, readyEntidad] = useEntidad({
        entidades: ['OrganoJudicial','TipoControlador','Controlador'],
        onLoaded: (entidades, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'OrganoJudicial_TipoControlador_Controlador',
          timeout: 0
        }
    });

    useEffect(() => {
        const idsTipoControladorAbogado = getListEntidad('TipoControlador').filter(f => f.abogado || f.oficialJusticia).map(x => x.id);
        const idsPersonaAbogado = getListEntidad('Controlador').filter(f => idsTipoControladorAbogado.includes(f.idTipoControlador)).map(x => x.idPersona);
        setPersonasControladores(idsPersonaAbogado); //las personas que son abogados u oficial de justicia
    }, [readyEntidad]);

    //#endregion

    const getDescOrganismoJudicial = (id) => {
        const row = getRowEntidad('OrganoJudicial', id);
        return (row) ? `${row.codigoOrganoJudicial} - ${row.departamentoJudicial}` : '';
    }

    const getDescFuero = (id) => {
        const row = getRowEntidad('OrganoJudicial', id);
        return (row) ? row.fuero : '';
    }

    //definiciones
    const tableColumns = [
        { Header: 'Apremio', accessor: 'numero', width: '10%' },
        { Header: 'Inicio de demanda', Cell: (data) => (data.value) ? getDateToString(data.value, false) : '', accessor: 'fechaInicioDemanda', width: '15%' },
        { Header: 'Organismo judicial', Cell: (data) => getDescOrganismoJudicial(data.value), accessor: 'idOrganismoJudicial', width: '20%' },
        { Header: 'Fuero', Cell: (data) => getDescFuero(data.value), id:'fuero', accessor: 'idOrganismoJudicial', width: '30%' },
        { Header: 'Abogado', accessor: 'nombrePersonaAbogado', width: '15%' },
    ];

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }

    const SearchApremiosWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.APREMIO,
        buildParamsUrl: (filters) => {
            const formatDate = (date) => {
                if (!date) return '';
                const d = new Date(date);
                return d.toISOString().split('T')[0];
            };
            return `/filter?` +
                `numero=${encodeURIComponent(filters.numero)}&` +
                `caratula=${encodeURIComponent(filters.caratula)}&` +
                `idExpediente=${filters.idExpediente}&` +
                `idOrganismoJudicial=${filters.idOrganismoJudicial}&` +
                `idPersonaAbogado=${filters.idPersonaAbogado}&` +
                `fechaInicioDemandaDesde=${encodeURIComponent(formatDate(filters.fechaInicioDemandaDesde))}&` +
                `fechaInicioDemandaHasta=${encodeURIComponent(formatDate(filters.fechaInicioDemandaHasta))}`;
        },
        callbackNoSuccess,
        callbackError
    });

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchApremiosWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    //handles
    const avmr = {
        onView: ({ id }) => {
            navigateWithFilters(`/apremio/${OPERATION_MODE.VIEW}/${id}`)
        },
        onModify: ({ id }) => {
            navigateWithFilters(`/apremio/${OPERATION_MODE.EDIT}/${id}`)
        },
        onRemove: ({ id }) => {
            const apremio = state.list.find(x => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar el apremio ${apremio.numero}?`,
                onConfirm: () => { RemoveApremio(id) }
            })
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setState(prevState => ({...prevState, dataTagger: { showModal: true, idEntidad: id, disabled }}))
        },
    }

    //funciones
    function SearchApremios() {
        SearchApremiosWithSavedFilters(getCurrentFilters());
    }

    function RemoveApremio(id) {
        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchApremios();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.APREMIO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? `el apremio ${dataTaggerRow.numero}` : ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.dataTagger.showModal &&
            <DataTaggerModalApi
                title="Información adicional de Apremio"
                registro={dataTaggerRegistro}
                tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_APREMIO_MODAL}
                entidad="Apremio"
                idEntidad={state.dataTagger.idEntidad}
                disabled={state.dataTagger.disabled}
                onConfirm={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
            />
        }

        <SectionHeading titles={[{ title: 'Legales' }, { title: 'Apremios' }]} />

        <section className='section-accordion'>

            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { title: 'Número', field: 'numero', type: 'string', size: 2, searchOnEnter: true },
                    { title: 'Carátula', field: 'caratula', type: 'string', size: 4, searchOnEnter: true },
                    { title: 'Expediente', field: 'idExpediente', type: 'expediente', size: 2 },
                    { 
                        title: 'Organismo judicial', 
                        field: 'idOrganismoJudicial', 
                        type: 'entity', 
                        entidad: 'OrganoJudicial', 
                        tipName: TIPS.NAME.MODALS.ORGANISMO_JUDICIAL_MODAL,
                        onLabelFormat: (row) => row ? `${row.codigoOrganoJudicial} - ${row.departamentoJudicial}` : '',
                        onFormat: (row) => row ? `${row.codigoOrganoJudicial} - ${row.departamentoJudicial}` : '',
                        columns: [
                            { Header: 'Código', accessor: 'codigoOrganoJudicial', width: '25%' },
                            { Header: 'Departamento judicial', accessor: 'departamentoJudicial', width: '50%' },
                            { Header: 'Secretaría', accessor: 'secretaria', width: '25%' }
                        ],
                        memo: false,
                        size: 4
                    },
                    { title: 'Abogado', field: 'idPersonaAbogado', type: 'persona', idTipoPersona: 0, size: 4 },
                    { title: 'Fecha desde', field: 'fechaInicioDemandaDesde', type: 'date', size: 2 },
                    { title: 'Fecha hasta', field: 'fechaInicioDemandaHasta', type: 'date', minValueField: 'fechaInicioDemandaDesde', size: 2 }
                ]}
                onSearch={SearchApremios}
            />

            {tableVisible && (
                <TableCustom
                    ref={tableRef}
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={state.list}
                    avmr={avmr}
                    editCode="apremio_edit"
                    restoredPageIndex={restoredPageIndex}
                    defaultGlobalFilter={restoredGlobalFilter}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
        
    </>
    )
}

export default ApremiosView;
